
'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Sparkles, Brush, Heart, Wand, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function CreativePlayfulHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-yellow-50 text-gray-800 overflow-x-hidden">
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&display=swap');
        .font-fredoka { font-family: 'Fredoka One', cursive; }
        .animate-wiggle {
          animation: wiggle 3s ease-in-out infinite;
        }
        .animate-bounce-slow {
          animation: bounce 4s ease-in-out infinite;
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(-3deg); }
          50% { transform: rotate(3deg); }
        }
      `}</style>
      <header className="p-6 flex justify-between items-center sticky top-0 bg-yellow-50/80 backdrop-blur-sm z-10">
        <h2
          className="text-3xl font-bold text-pink-500 font-fredoka"
        >
          Wobble
        </h2>
        <nav className="hidden md:block">
            <Button className="bg-blue-400 hover:bg-blue-500 text-white rounded-full px-6 transition-transform hover:scale-105">
              Get Started
            </Button>
        </nav>
        <div className="md:hidden">
             <Button size="icon" variant="ghost" onClick={() => setIsMenuOpen(true)}>
                 <Menu className="text-pink-500"/>
            </Button>
        </div>
      </header>

      {isMenuOpen && (
           <div className="fixed inset-0 bg-yellow-100 z-50 flex flex-col items-center justify-center gap-8">
                <Button size="icon" variant="ghost" onClick={() => setIsMenuOpen(false)} className="absolute top-6 right-6">
                    <X className="text-pink-500" size={32}/>
                </Button>
                <h2 className="text-4xl font-bold text-pink-500 font-fredoka">Wobble</h2>
                <Button size="lg" className="bg-blue-400 hover:bg-blue-500 text-white rounded-full px-8 text-xl">
                  Get Started
                </Button>
           </div>
      )}

      <main>
        <section className="py-20 px-6">
          <div className="text-center">
            <h1 className="text-5xl md:text-7xl font-bold leading-tight font-fredoka">
              Where Ideas
              <br />
              <span className="text-orange-500">Go Wild!</span>
            </h1>
            <p className="mt-4 max-w-lg mx-auto text-gray-600">
              A delightfully chaotic toolkit for brainstorming, doodling, and
              making your next big thing.
            </p>
            <div className="mt-12 relative inline-block animate-wiggle">
              <Image
                src="https://picsum.photos/seed/play4/600/400"
                alt="Colorful abstract shapes and scribbles"
                width={600}
                height={400}
                className="rounded-full shadow-2xl w-64 h-64 md:w-[600px] md:h-[400px] object-cover"
                data-ai-hint="abstract scribbles"
              />
              <div className="absolute -top-8 -left-8 w-16 h-16 md:w-24 md:h-24 bg-pink-300 rounded-full animate-bounce-slow"></div>
              <div className="absolute -bottom-4 -right-4 w-12 h-12 md:w-16 md:h-16 bg-teal-300 rounded-full animate-pulse"></div>
            </div>
          </div>
        </section>

        <section className="py-24 bg-white px-4">
            <div className="container mx-auto text-center">
                <h2 className="text-4xl font-bold mb-12 font-fredoka">What's Inside?</h2>
                <div className="grid md:grid-cols-3 gap-10">
                    <div className="text-center">
                        <div className="w-24 h-24 bg-orange-200 rounded-full mx-auto flex items-center justify-center mb-4 transform hover:scale-110 transition-transform">
                            <Sparkles className="w-12 h-12 text-orange-600" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Infinite Canvas</h3>
                        <p className="text-gray-500">Never run out of space for your brilliant (and not-so-brilliant) ideas.</p>
                    </div>
                    <div className="text-center">
                        <div className="w-24 h-24 bg-pink-200 rounded-full mx-auto flex items-center justify-center mb-4 transform hover:scale-110 transition-transform">
                            <Brush className="w-12 h-12 text-pink-600" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Silly Stickers</h3>
                        <p className="text-gray-500">Why use words when a dancing taco will do? Exactly.</p>
                    </div>
                    <div className="text-center">
                        <div className="w-24 h-24 bg-blue-200 rounded-full mx-auto flex items-center justify-center mb-4 transform hover:scale-110 transition-transform">
                            <Heart className="w-12 h-12 text-blue-600" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Chaos Control</h3>
                        <p className="text-gray-500">Organize your beautiful mess with tags, colors, and magic.</p>
                    </div>
                </div>
            </div>
        </section>

        <section className="bg-teal-400 text-white py-20 px-6 transform -rotate-2 scale-105 my-20">
            <div className="container mx-auto text-center transform rotate-2">
                <Wand className="w-16 h-16 mx-auto mb-4" />
                <h2 className="text-4xl font-bold mb-4 font-fredoka">Free for Everyone!</h2>
                <p className="max-w-md mx-auto mb-8">
                    Yep, you heard that right. All this creative goodness, totally free. 
                    Go on, make something amazing.
                </p>
                <Button className="bg-white text-teal-500 hover:bg-yellow-200 rounded-full px-8 py-3 text-lg transition-transform hover:scale-105">
                    Start Creating Now!
                </Button>
            </div>
        </section>
      </main>
      <footer className="bg-gray-800 text-white py-10 px-6 text-center">
        <p>Made with 💖 and a little bit of madness by the Wobble team.</p>
        <p className="text-sm text-gray-400 mt-2">&copy; {new Date().getFullYear()} Wobble Inc.</p>
      </footer>
    </div>
  );
}
